import { InputAdornment, type SxProps, TextField } from "@mui/material";
import { useController } from "react-hook-form";
import {
  type CommonInputProps,
  composeValidators,
  isRequired,
  sanitizeInputRestProps,
  useRecordContext,
  ValidationError,
} from "react-admin";
import _get from "lodash/get";
import { centsToMoney, isMoney, type Money } from "../model/Money";
import { type Cents } from "../model/Cents";
import { useCallback, useRef, useState } from "react";

interface MoneyInputProps extends Omit<CommonInputProps, "parse" | "format" | "defaultValue" | "variant"> {
  placeholder?: string;
  sx?: SxProps;
  size?: "medium" | "small";
  autoFocus?: boolean;
}

/** Use this input component for `Cents` properties. */
export function CentsInput(props: MoneyInputProps) {
  return <_MoneyInput<Cents> {...props} format={convertCentsToString} parse={convertStringToCents} />;
}

/** Use this input component when you need a money amount as a number in EUR. */
export function EurosInput(props: MoneyInputProps) {
  return <_MoneyInput<number> {...props} format={convertEurosToString} parse={convertStringToEuros} />;
}

/** Use this input component for `Money` properties. */
export function MoneyInput(props: MoneyInputProps) {
  return <_MoneyInput<Money> {...props} format={convertMoneyToString} parse={convertStringToMoney} />;
}

function _MoneyInput<T>(
  props: MoneyInputProps & {
    format: (value?: null | T) => string;
    parse: (value: string) => null | T;
  },
) {
  const {
    label,
    helperText,
    onBlur,
    onChange,
    source,
    validate: customValidate,
    format,
    parse,
    autoFocus,
    sx,
    ...rest
  } = props;
  const record = useRecordContext();
  const initialValue = _get(record, source);
  const [isFocused, setIsFocused] = useState(false);
  const [rawInput, setRawInput] = useState("");
  const rawInputRef = useRef("");
  const validate = useCallback(
    (value: any, values: any) => {
      if (rawInputRef.current && (value === null || typeof value === "undefined")) {
        return "Ungültige Eingabe";
      }
      if (customValidate) {
        const validateFunction = Array.isArray(customValidate) ? composeValidators(customValidate) : customValidate;
        const error = validateFunction(value, values, props);
        if (error) {
          if (typeof error === "string") {
            return error;
          } else {
            // react-hook-form expects errors to be strings but react-admin validators
            // can return objects that have message and args. The ValidationError component
            // will check for the following format and extract the message and args again ...
            return `@@react-admin@@${JSON.stringify(error)}`;
          }
        }
      }
    },
    [customValidate],
  );
  const required = props.isRequired || isRequired(customValidate);
  const {
    field: fieldProps,
    fieldState: { error, invalid },
  } = useController({
    name: props.name || source,
    defaultValue: initialValue,
    rules: { validate },
    disabled: props.disabled,
  });
  const setInputValue = (newInputValue: string) => {
    setRawInput(newInputValue);
    rawInputRef.current = newInputValue;
    const parsedValue = parse(newInputValue);
    fieldProps.onChange(parsedValue);
    onChange?.(parsedValue);
  };
  // When blurred, display is derived from the form value — no sync needed.
  const displayValue = isFocused ? rawInput : format(fieldProps.value);
  return (
    <TextField
      id={source}
      sx={{
        ...sx,
        "& input": { textAlign: "right" },
      }}
      {...fieldProps}
      value={displayValue}
      label={label}
      placeholder={props.placeholder}
      variant="outlined"
      autoFocus={autoFocus}
      required={required}
      error={invalid}
      helperText={
        error ? (
          <ValidationError error={error.message || "Ungültige Eingabe"} />
        ) : (
          helperText || " " /* ... to prevent jumping forms */
        )
      }
      {...sanitizeInputRestProps(rest)}
      InputProps={{
        endAdornment: <InputAdornment position="end">€</InputAdornment>,
      }}
      onChange={(event) => setInputValue(event.target.value)}
      onFocus={() => {
        setRawInput(format(fieldProps.value));
        rawInputRef.current = format(fieldProps.value);
        setIsFocused(true);
      }}
      onBlur={() => {
        setIsFocused(false);
        fieldProps.onBlur();
        onBlur?.();
      }}
    />
  );
}

const _nf = new Intl.NumberFormat("de", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function convertCentsToString(cents?: null | Cents): string {
  if (typeof cents === "number" && isFinite(cents)) {
    return convertMoneyToString(centsToMoney(cents));
  }
  return "";
}

function convertEurosToString(euros?: null | number): string {
  if (typeof euros === "number" && isFinite(euros)) {
    return convertMoneyToString({ amount: euros, currency: "EUR" });
  }
  return "";
}

// visible for testing
export function convertMoneyToString(money?: null | Money): string {
  if (isMoney(money) && isFinite(money.amount)) {
    return _nf.format(money.amount);
  }
  if (money) {
    throw new Error("Invalid money: " + JSON.stringify(money));
  }
  return "";
}

function convertStringToCents(inputValue: string): null | Cents {
  const money = convertStringToMoney(inputValue);
  return money && money.amount * 100;
}

// visible for testing
export function convertStringToMoney(inputValue: string): null | Money {
  const amount = convertStringToEuros(inputValue);
  return typeof amount === "number" ? { amount, currency: "EUR" } : null;
}

const convertStringToEuros = (inputValue: string): null | number => {
  if (inputValue) {
    const value = inputValue.toString().trim();
    const germanFormat = value.match(/^-?(\d+)(\.\d{3})*(,\d\d)?$/);
    const usFormat = value.match(/^-?(\d+)(,\d{3})*(\.\d\d)?$/);
    if (!germanFormat && !usFormat) {
      return null;
    }
    let normalizedValue = value.replaceAll(" ", "").replaceAll("_", "");
    if (germanFormat) {
      normalizedValue = normalizedValue.replaceAll(".", "").replace(",", ".");
    } else if (usFormat) {
      normalizedValue = value.replaceAll(",", "");
    }
    const amount = parseFloat(normalizedValue);
    if (typeof amount === "number" && isFinite(amount)) {
      return amount;
    }
  }
  return null;
};
